// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 알림 한 장 (오류 · 주의 · 정상 · 안내)
//
//  ★ 화면에서 `<Alert color="red">` 를 쓰지 마라. **이걸 써라.**
//
//    옛 Login.tsx:297 이 `<Alert color="red">` 였다. 그래서 우리 `--cv-danger`(6.44:1)는
//    **제품 어디에도 안 나왔고**, 대신 Mantine 기본 red 로 그려졌다:
//
//        Mantine light red (red-9 on red-1)   실내 4.51 · 20klux 2.35 · 50klux 1.65
//        --cv-danger 로 그렸다면              실내 6.44 · 20klux 2.70 · 50klux 1.77
//
//    4.51 은 AA(4.5)를 **0.01 로** 넘긴 값이다. 즉 "danger 를 warn 에서 분리했다" 는
//    결정이 문서에만 있고 코드에는 없었다. tone 을 타입으로 강제하면 그 일이 안 생긴다.
//
//  ★ tone 넷의 뜻이 서로 다르다. 색이 아니라 **뜻**으로 골라라 —
//    야외에서는 색이 뭉개지므로, 결국 사람을 구하는 것은 문구와 위치다.
//
//      info   … 지금 무슨 일이 있었는지 알린다. 사용자가 할 일은 없다
//      ok     … 끝났다
//      warn   … 되돌릴 수 있는 문제. 다시 하면 된다
//      danger … **되돌릴 수 없거나**, 사람이 지금 뭔가 해야 한다
//
//  ⚠️ warn 과 danger 를 섞지 마라. 현장 정지·구성원 회수·초대 무효화는 되돌릴 수 없다.
//     그 셋이 "다시 하면 되는 일" 과 같은 색이면 사람이 구분할 방법이 화면에 없다.
// ─────────────────────────────────────────────────────────────────────────

import { Alert, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';

export type Tone = 'info' | 'ok' | 'warn' | 'danger';

/** tone → theme.ts 의 Alert `vars` 가 알아듣는 색 이름. info 는 모르는 이름이라 강조색이 된다. */
const COLOR: Record<Tone, string> = {
  info: 'cv',
  ok: 'ok',
  warn: 'warn',
  danger: 'danger',
};

export function Notice({
  tone = 'info',
  title,
  children,
  /**
   * 스크린리더가 **즉시** 읽게 할지. 기본은 warn·danger 만 true 다.
   *
   * ★ 오류를 눈으로만 알리면 확대해서 쓰는 사람은 화면 밖의 빨간 글씨를 영영 못 본다.
   * ⚠️ 반대로 모든 안내를 alert 로 만들면 스크린리더가 쉬지 않고 떠들어서
   *    정작 오류가 묻힌다. 그래서 기본값이 tone 에 따라 갈린다.
   */
  live,
  /**
   * 닫기 단추. **주면 나오고, 안 주면 안 나온다.**
   *
   * ★ 이게 없어서 「초대 결과」 한 자리가 raw `<Alert color="cv">` 로 남아 있었다.
   *   화면에 raw Alert 가 한 장이라도 남아 있으면 그게 다음 사람의 본보기가 되고,
   *   그 사람은 `color="red"` 를 적는다 — 실제로 그렇게 아홉 자리가 생겼다.
   *   **그래서 예외를 없애는 쪽이 색을 고치는 것만큼 중요하다.**
   *
   * ⚠️ warn·danger 에는 웬만하면 주지 마라. 「닫을 수 있는 위험」은 안 읽고 닫힌다.
   *    닫아도 되는 것은 「끝난 일의 보고」(ok·info)다.
   */
  onClose,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  live?: boolean;
  onClose?: () => void;
}) {
  const urgent = live ?? (tone === 'warn' || tone === 'danger');

  return (
    <Alert
      color={COLOR[tone]}
      role={urgent ? 'alert' : 'status'}
      // 조용한 안내는 읽던 문장을 끊지 않게 polite 로. 오류만 끼어든다.
      aria-live={urgent ? 'assertive' : 'polite'}
      title={title}
      withCloseButton={!!onClose}
      onClose={onClose}
      // ⚠️ 아이콘만 있는 닫기 단추는 스크린리더에 "버튼" 으로만 읽힌다. 무엇을 닫는지 말해 준다.
      closeButtonLabel="이 알림 닫기"
    >
      {children}
    </Alert>
  );
}

/**
 * 알림 안에서 **덜 중요한 한 줄**을 쓸 때.
 *
 * ⚠️ 여기서도 `c="dimmed"` 를 쓰지 마라. 알림 안의 글은 전부 「사람이 막혔을 때 읽는 글」이라
 *    야외 A/B 등급을 지켜야 한다. 14px + --cv-ink-2 가 하한이다.
 *    (옛 코드의 스팸함 안내가 `size="xs" c="dimmed"` = 12px muted = 야외 2.86:1 · 직사광
 *     1.46:1 이었다. 가장 흔한 실패를 푸는 문장이 화면에서 가장 안 보이는 글자였다.)
 */
export function NoticeDetail({ children }: { children: ReactNode }) {
  return (
    <Text size="sm" c="var(--cv-ink-2)">
      {children}
    </Text>
  );
}

/** 알림 본문이 여러 줄일 때 간격을 맞춘다. */
export function NoticeLines({ children }: { children: ReactNode }) {
  return <Stack gap="xs">{children}</Stack>;
}
